import React, { useState } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { useAsync } from '../aai/useAsync';
import type { GrantsView } from '../../types/aai';
import { Chip, EmptyState, ErrorNote, Loading, Panel, PanelHead } from '../../components/aa';
import { hasScreens } from '../../lib/rolesWithoutScreens';

/*
 * What each role may see and do at one company. The platform default is the
 * row; a yellow mark means this company overrides it. The super admin edits
 * every cell. A holder of "manage grants below me" edits only roles below
 * theirs, and only grants they hold themselves.
 */

const ROOM_PREFIX = 'aai_room_';

export const GrantsPanel: React.FC<{
  /** Company to read. Omitted for callers whose API scope already names one. */
  companyId?: string;
  className?: string;
  /** Leave out roles that have no screens yet (the super admin's view). */
  onlyBuiltRoles?: boolean;
}> = ({ companyId, className = '', onlyBuiltRoles = false }) => {
  const grants = useAsync<GrantsView>(() => aaiApi.getGrants(companyId), 'Could not load grants.', [companyId]);
  const [filter, setFilter] = useState<'room' | 'all'>('room');
  const [busy, setBusy] = useState<string | null>(null);

  if (grants.loading) return <Panel className={className}><Loading label="Loading grants" /></Panel>;
  if (grants.error || !grants.data) return <Panel className={className}><div className="px-6 py-5"><ErrorNote message={grants.error || 'Could not load grants.'} /></div></Panel>;

  const data = grants.data;
  let roles = data.roles;
  if (onlyBuiltRoles) roles = roles.filter((r) => hasScreens(r.value));
  let permissions = data.permissions;
  if (filter === 'room') permissions = permissions.filter((p) => p.id.startsWith(ROOM_PREFIX) || p.id === 'aai_manage_grants_below');

  const toggle = async (role: string, permission: string, next: boolean) => {
    const key = `${role}:${permission}`;
    setBusy(key);
    try {
      const roleRow = data.roles.find((r) => r.value === role);
      const isDefault = Boolean(roleRow && roleRow.default.includes(permission));
      let allowed: boolean | null = next;
      if (next === isDefault) allowed = null;
      await aaiApi.setGrant(role, permission, allowed, companyId);
      grants.reload();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Could not change that grant.');
    } finally {
      setBusy(null);
    }
  };

  let desc = 'Platform defaults apply everywhere. A yellow mark means this company overrides the default. Scope (own department, own answers) comes from the roster, not from here.';
  if (!data.can_manage) desc = 'Read only. Grants are managed by the platform, or by someone here who holds "manage grants below me".';

  return (
    <Panel className={className}>
      <PanelHead
        kicker="Grants"
        title="What each role may see and do"
        desc={desc}
        action={(
          <div className="inline-flex gap-0.5 rounded-act border border-line bg-canvas-inset p-[3px]">
            {[{ id: 'room', label: 'Orbit Room' }, { id: 'all', label: 'All grants' }].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id as 'room' | 'all')}
                className={clsx('h-7 rounded-card px-3 text-[12.5px] font-semibold transition-colors', filter === f.id && 'bg-white text-ink shadow-[inset_0_-2px_#18191D]', filter !== f.id && 'text-ink-muted hover:text-ink')}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      />
      {permissions.length === 0 && <EmptyState title="No grants to show" reason="The grant catalog is empty for this filter." />}
      {permissions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                <th className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft md:px-6">Grant</th>
                {roles.map((r) => (
                  <th key={r.value} className="whitespace-nowrap px-3 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{r.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissions.map((p) => (
                <tr key={p.id} className="border-t border-line-soft">
                  <td className="px-5 py-3 md:px-6">
                    <div className="font-semibold text-ink">{p.name}</div>
                    <div className="text-[12px] text-ink-dim">{p.description}</div>
                  </td>
                  {roles.map((r) => {
                    const on = r.effective.includes(p.id);
                    const overridden = p.id in r.overrides;
                    const key = `${r.value}:${p.id}`;
                    const canEdit = data.can_manage && r.manageable && (data.actor_grants === null || data.actor_grants.includes(p.id));
                    let track = 'bg-input';
                    if (on) track = 'bg-ink';
                    if (overridden) track = 'bg-accent-500';
                    let knob = 'left-0.5 bg-white';
                    if (on) knob = 'left-[18px] bg-white';
                    if (on && overridden) knob = 'left-[18px] bg-ink';
                    if (!on && overridden) knob = 'left-0.5 bg-ink';
                    return (
                      <td key={key} className="px-3 py-3 text-center">
                        <button
                          type="button"
                          aria-label={`${p.name} for ${r.label}`}
                          aria-pressed={on}
                          disabled={!canEdit || busy === key}
                          onClick={() => toggle(r.value, p.id, !on)}
                          className={clsx('relative inline-block h-5 w-9 rounded-full transition-colors disabled:opacity-40', track)}
                        >
                          <span className={clsx('absolute top-0.5 h-4 w-4 rounded-full transition-all', knob)} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 border-t border-line-soft px-5 py-3 text-[12px] text-ink-dim md:px-6">
        <Chip tone="neutral">black: granted</Chip>
        <Chip tone="warn">yellow: overridden here</Chip>
        <span>Grey: not granted.</span>
      </div>
    </Panel>
  );
};

export default GrantsPanel;
